import React from "react";
import { motion } from "framer-motion";
import { ANIM, SPEED } from "./animations";

/** Looping keyframe animation, driven by Framer Motion. Extra props (className/style) pass through. */
export const Loop = ({ anim, dur, as = "div", children, ...rest }) => {
  const { times, ...keys } = ANIM[anim];
  const M = motion[as] || motion.div;
  return (
    <M animate={keys} transition={{ duration: dur * SPEED, times, repeat: Infinity, ease: "easeInOut" }} {...rest}>
      {children}
    </M>
  );
};

/**
 * Vertical odometer: the `odo5` Loop scrolls through `rows`. `size` picks the height/type
 * scale (26/28/32/34), `tones` is one tone for all digits or a per-digit array
 * (white | green-deep | navy | green), `mt` adds top margin, `align` right-aligns.
 */
export const Odometer = ({ size, dur, rows, tones, mt, align }) => {
  const toneOf = (i) => (Array.isArray(tones) ? tones[i] : tones);
  const wrap = ["abp-odo", `abp-odo--${size}`, mt && `abp-odo--mt${mt}`, align && "abp-odo--right"].filter(Boolean).join(" ");
  return (
    <div className={wrap}>
      <Loop anim="odo5" dur={dur}>
        {rows.map((v, i) => (
          <div key={i} className={`abp-odo__d abp-odo__d--${size} abp-odo__d--${toneOf(i)}`}>{v}</div>
        ))}
      </Loop>
    </div>
  );
};

/** Green-check bullet list shared by every feature section. */
export const CheckList = ({ items }) => {
  return (
    <ul className="abp-checklist">
      {items.map((text, i) => (
        <li key={i} className="abp-checklist__item">
          <span className="abp-checklist__mark">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M20 6 9 17l-5-5" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
          <span className="abp-checklist__text">{text}</span>
        </li>
      ))}
    </ul>
  );
};

/** Feature-section text column: eyebrow + heading + body + checklist, driven by content. */
export const SectionText = ({ feature }) => {
  return (
    <div className="abp-textcol">
      <div className="abp-eyebrow">{feature.eyebrow}</div>
      <h2 className="abp-h2">{feature.title}</h2>
      <p className="abp-lead">{feature.body}</p>
      <CheckList items={feature.bullets} />
    </div>
  );
};

/** Alternating feature row: text column + an illustration passed as children. */
export const FeatureSection = ({ feature, label, reversed, children }) => {
  return (
    <section data-screen-label={label} className={reversed ? "abp-section abp-section--reverse" : "abp-section"}>
      <SectionText feature={feature} />
      {children}
    </section>
  );
};
